<template>
    <Spinner v-if="!isDoneLoading" />
    <div class="meta-box">
        <h1>{{ processName }}</h1>
    </div>
    <div class="controls">
        <div
            id="properties-panel"
            ref="propertiesPanelElement"
            :class="{
                'visible': definition?.content && isEditing,
            }"
        />
        <Button
            v-for="button in buttons"
            :key="button.label"
            :class="button.class"
            :icon="button.icon"
            :label="button.label"
            @click="button.onClick"
        />
        <template v-if="definition?.content">
            <Button
                severity="secondary"
                icon="pi pi-ellipsis-v"
                ref="optionsButtonElement"
                @click="showMenu"
            />
            <ContextMenu ref="menu" :model="contextMenuItems" />
            <input
                name="file"
                type="file"
                accept=".bpmn"
                style="display: none"
                ref="fileUploadElement"
                @change="(e) => uploadProcessDefinition(e as HTMLInputEvent)"
            />
        </template>
    </div>
    <div class="definition" v-if="isDoneLoading">
        <template v-if="isViewingHistory">
            <template v-if="comparisonArtefact?.content">
                <ProcessDefinitionDiff
                    v-if="selectedArtefact?.content"
                    :from="comparisonArtefact.content"
                    :to="selectedArtefact.content"
                    ref="historicalDefinitionElement"
                    :showAsDiff="route.query.compare !== undefined"
                />
            </template>
            <!-- When there are no older artefacts, we can't show a meaningful
                diff yet. -->
            <template v-else>
                <ProcessDefinition
                    v-if="selectedArtefact?.content"
                    v-model:bpmnArtefact="selectedArtefact"
                    :isMaximizingEnabled="false"
                    ref="historicalDefinitionElement"
                />
            </template>
        </template>
        <template v-else>
            <ProcessDefinition
                v-if="definition?.content"
                v-model:bpmnArtefact="definition"
                :isMaximizingEnabled="false"
                ref="definitionElement"
            />
            <ProcessDefinitionCreator
                v-else-if="definition && process"
                v-model="definition"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
import ContextMenu from "primevue/contextmenu";
import { useConfirm } from "primevue/useconfirm";
import ProcessDefinition from "@/components/Process/Definition.vue";
import ProcessDefinitionDiff from "@/components/Diff/ProcessDefinitionDiff.vue";
import ProcessDefinitionCreator from "@/components/Process/DefinitionCreator.vue";
import {
    useProcessManager,
    useProcessStore,
} from "@/composables/process-manager";
import { computed, ref } from "vue";
import { downloadBlob } from "@/utils";
import Spinner from "@/shared/Spinner.vue";

import { useEditStore, useEditManager } from "@/composables/edit-manager";
import { useTaskManager } from "@/stores/task-store";
import { useRouter, useRoute, onBeforeRouteLeave } from "vue-router";
import { useHistoryManager, useHistoryStore } from "@/stores/history";
import Button, { ButtonProps } from "primevue/button";
import type { MenuItem } from "primevue/menuitem";
import { useArtefactsStore } from "@/lib/artefacts/user";
import { useGenerateBpmnDialog } from "@/composables/generate-bpmn-dialog";
import { useRolesManager } from "@/stores/roles-store";

const menu = ref<typeof ContextMenu>();
const optionsButtonElement = ref<typeof Button>();
const propertiesPanelElement = ref<HTMLElement>();
const { updateDefinition } = useProcessStore();
const { process, definition, isFetching } = useProcessManager();
const { roles } = useRolesManager();
const artefactsStore = useArtefactsStore();

const { startEditing } = useEditStore();
const { isEditing } = useEditManager();
const isRestoring = ref(false);
const router = useRouter();
const route = useRoute();

const { loadHistory } = useHistoryStore();
const {
    isViewingLatestVersion,
    isViewingHistory,
    selectedArtefact,
    comparisonArtefact,
    isFetchingHistory,
    isFetchingHistoricalArtefact,
} = useHistoryManager();
const { open: openGenerateDialog } = useGenerateBpmnDialog({
    roles,
    artefact: definition,
});

const fileUploadElement = ref<HTMLElement>();

/**
 * Check if the given filelist contains exactly one BPMN file.
 */
const isBpmnFile = (files?: FileList | null) => {
    if (!files || files.length !== 1) {
        return false;
    }
    return files[0].name.endsWith(".bpmn");
};

const uploadProcessDefinition = async (event: HTMLInputEvent) => {
    if (!event.target.files || !isBpmnFile(event.target.files)) {
        return;
    }
    await definition.value?.updateContent(event.target.files[0], true);
    startEditing();
};

const isDoneLoading = computed(() => {
    return (
        !isFetching.value &&
        !isFetchingHistory.value &&
        !isFetchingHistoricalArtefact.value &&
        !isRestoring.value
    );
});

useTaskManager();

const showMenu = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    const rect = optionsButtonElement.value?.$el.getBoundingClientRect();
    // Fake the event that is consumed by 'show', to always position the context menu at
    // the bottom right of the clicked button vs where is clicked on the button.
    const fakeEvent = {
        pageX: rect.left + rect.width,
        pageY: rect.top + rect.height,
        stopPropagation(): void {},
        preventDefault(): void {},
    };
    menu.value?.show(fakeEvent);
};

const confirm = useConfirm();

const processName = computed(() => {
    if (!process.value) {
        return "...";
    }

    return process.value.name;
});

const downloadAsBpmn = () => {
    if (process.value && selectedArtefact.value) {
        downloadBlob(
            URL.createObjectURL(selectedArtefact.value?.blob),
            `${process.value?.name}.bpmn`
        );
    }
};

const definitionElement = ref<typeof ProcessDefinition>();
const historicalDefinitionElement = ref<typeof ProcessDefinition>();
const selectedDefinitionElement = computed(() => {
    return isViewingHistory.value
        ? historicalDefinitionElement.value
        : definitionElement.value;
});

const downloadAsSvg = async () => {
    if (!selectedDefinitionElement.value || !process.value) {
        return;
    }
    const svg = await selectedDefinitionElement.value.getSvg();
    downloadBlob(
        URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" })),
        `${process.value.name}.svg`
    );
};

const downloadAsPng = async () => {
    if (!selectedDefinitionElement.value) {
        return;
    }
    const svg = await selectedDefinitionElement.value.getSvg();
    const blob = new Blob([svg], { type: "image/svg+xml" });

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d") as CanvasRenderingContext2D;
    const img = new Image();
    img.addEventListener("load", () => {
        canvas.width = img.width;
        canvas.height = img.height;
        context.drawImage(img, 0, 0);
        downloadBlob(
            canvas.toDataURL("image/png"),
            `${process.value?.name}.png`
        );
    });
    img.addEventListener("error", (event) => {
        console.error("Cannot save as PNG: ", event);
    });
    img.src = URL.createObjectURL(blob);
};

onBeforeRouteLeave(async () => {
    await definitionElement.value?.completeActiveActions();
});

const restoreConfirmation = async () => {
    return new Promise((resolve) => {
        confirm.require({
            message: "Are you sure you want restore this version?",
            header: "Restore version",
            icon: "pi pi-exclamation-triangle",
            rejectProps: {
                label: "Cancel",
                severity: "secondary",
                outlined: true,
            },
            acceptProps: {
                label: "Restore",
            },
            accept: () => {
                resolve(true);
            },
            reject: () => {
                resolve(false);
            },
            onHide: () => {
                resolve(false);
            },
        });
    });
};

const buttonsAndTheirConditions = [
    {
        class: "p-button-secondary",
        icon: "pi pi-check",
        label: "Restore",
        click: async () => {
            const shouldRestore = await restoreConfirmation();
            if (!shouldRestore) {
                return;
            }

            isRestoring.value = true;
            try {
                await artefactsStore.restoreArtefact(selectedArtefact.value!);
                await Promise.all([updateDefinition(), loadHistory()]);
                await router.push({
                    name: route.name,
                    params: {
                        ...route.params,
                        version: "current",
                    },
                    query: route.query,
                });
            } finally {
                isRestoring.value = false;
            }
        },
        condition: () => !isViewingLatestVersion.value,
    },
];

const buttons = computed<ButtonProps[]>(() => {
    return buttonsAndTheirConditions
        .filter((button) => !button.condition || button.condition())
        .map((button) => {
            return {
                class: button.class,
                icon: button.icon,
                label: button.label,
                onClick: button.click,
            };
        });
});

const contextMenuItemsAndTheirConditions = [
    {
        label: "Download as SVG",
        icon: "pi pi-image",
        command: downloadAsSvg,
    },
    {
        label: "Download as PNG",
        icon: "pi pi-image",
        command: downloadAsPng,
    },
    {
        label: "Download as BPMN",
        icon: "pi pi-download",
        command: downloadAsBpmn,
    },
    {
        label: "Upload a BPMN diagram",
        icon: "pi pi-upload",
        command: () => fileUploadElement.value?.click(),
        condition: () => isViewingLatestVersion.value,
    },
    {
        label: "Regenerate process",
        icon: "pi pi-sparkles",
        command: () => openGenerateDialog(),
        condition: () => isViewingLatestVersion.value,
    },
    {
        label: "History",
        icon: "pi pi-history",
        command: () => {
            router.push({
                name: route.name,
                params: {
                    ...route.params,
                    version: "current",
                },
            });
        },
        condition: () => !isViewingHistory.value && !isEditing.value,
    },
];

const contextMenuItems = computed<MenuItem[]>(() => {
    return contextMenuItemsAndTheirConditions
        .filter((item) => !item.condition || item.condition())
        .map((item) => {
            return {
                label: item.label,
                icon: item.icon,
                command: item.command,
            };
        });
});
</script>

<style scoped lang="scss">
.title {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.controls {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    padding: 0.5rem 0;
    margin-bottom: 1rem;
}

.definition {
    flex: 1;
    display: flex;
}
</style>
